import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  Pagination,
  Typography,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

const ITEMS_PER_PAGE = 8;

const PushNotificationResultsDialog = ({ open, onClose, results }) => {
  const [successPage, setSuccessPage] = useState(1);
  const [failedPage, setFailedPage] = useState(1);

  const successfulNotifications = useMemo(
    () => results?.successful_notifications || [],
    [results]
  );
  const failedNotifications = useMemo(
    () => results?.failed_notifications || [],
    [results]
  );

  const successfulTotalPages = Math.ceil(successfulNotifications.length / ITEMS_PER_PAGE) || 1;
  const failedTotalPages = Math.ceil(failedNotifications.length / ITEMS_PER_PAGE) || 1;

  const paginatedSuccess = useMemo(() => {
    const start = (successPage - 1) * ITEMS_PER_PAGE;
    return successfulNotifications.slice(start, start + ITEMS_PER_PAGE);
  }, [successPage, successfulNotifications]);

  const paginatedFailed = useMemo(() => {
    const start = (failedPage - 1) * ITEMS_PER_PAGE;
    return failedNotifications.slice(start, start + ITEMS_PER_PAGE);
  }, [failedPage, failedNotifications]);

  if (!results) return null;

  const summary = results.summary || {};
  const successfulCount =
    summary.successful_count ??
    results.success ??
    successfulNotifications.length;
  const failedCount =
    summary.failed_count ??
    results.failure ??
    failedNotifications.length;
  const totalCount =
    summary.total_notifications ??
    results.attempted_tokens ??
    successfulNotifications.length + failedNotifications.length;

  const getStatusColor = () => {
    if ((failedCount || 0) === 0) return 'success';
    if ((successfulCount || 0) === 0) return 'error';
    return 'warning';
  };

  const getStatusIcon = () => {
    if ((failedCount || 0) === 0) return <SuccessIcon color="success" />;
    if ((successfulCount || 0) === 0) return <ErrorIcon color="error" />;
    return <InfoIcon color="warning" />;
  };

  const renderList = (items, type) => {
    if (!items.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No hay registros para mostrar.
        </Typography>
      );
    }

    return (
      <List dense>
        {items.map((item, idx) => (
          <ListItem key={`${type}-${item.device_token || item.uuid_subscriber}-${idx}`} sx={{ px: 0 }}>
            <Card
              sx={{
                width: '100%',
                border: 1,
                borderColor: type === 'success' ? 'success.light' : 'error.light',
                backgroundColor: type === 'success' ? '#f8fdf8' : '#fef8f8',
              }}
              variant="outlined"
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon fontSize="small" color="primary" />
                    <Typography variant="body2" fontWeight={600}>
                      {item.subscriber_name || 'Suscriptor sin nombre'}
                    </Typography>
                  </Box>
                  <Chip
                    label={type === 'success' ? 'Enviado' : 'Error'}
                    color={type === 'success' ? 'success' : 'error'}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                <Typography variant="caption" color="text.secondary">
                  UUID: {item.uuid_subscriber}
                </Typography>

                {item.subscriber_email && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Email: {item.subscriber_email}
                  </Typography>
                )}

                {item.error && (
                  <Typography variant="caption" color="error.main" display="block" mt={0.5}>
                    Error: {item.error}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Resultado de envío</DialogTitle>
      <DialogContent>
        <Alert
          icon={getStatusIcon()}
          severity={getStatusColor()}
          sx={{ mb: 3 }}
        >
          {results.message || 'Proceso finalizado.'}
        </Alert>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Total
                </Typography>
                <Typography variant="h6">{totalCount ?? 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Exitosas
                </Typography>
                <Typography variant="h6" color="success.main">
                  {successfulCount ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Fallidas
                </Typography>
                <Typography variant="h6" color="error.main">
                  {failedCount ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box mb={3}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            Envíos exitosos
          </Typography>
          {renderList(paginatedSuccess, 'success')}
          {successfulNotifications.length > ITEMS_PER_PAGE && (
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <Pagination
                size="small"
                count={successfulTotalPages}
                page={successPage}
                onChange={(_, page) => setSuccessPage(page)}
              />
            </Box>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            Envíos con error
          </Typography>
          {renderList(paginatedFailed, 'failed')}
          {failedNotifications.length > ITEMS_PER_PAGE && (
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <Pagination
                size="small"
                count={failedTotalPages}
                page={failedPage}
                onChange={(_, page) => setFailedPage(page)}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PushNotificationResultsDialog;
